import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import authService, { User, LoginCredentials } from '@/services/auth'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  refetch: () => Promise<any>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  // Query to get user profile
  const { 
    data: userData, 
    isLoading: profileLoading,
    refetch: refetchProfile,
    error: profileError
  } = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: authService.getProfile,
    enabled: authService.isAuthenticated(),
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      setUser(data.user)
      queryClient.setQueryData(['auth', 'profile'], data.user)
      toast.success(`Welcome back, ${data.user.firstName}!`)
    },
    onError: (error: any) => {
      console.error('Login failed:', error)
      toast.error(error.response?.data?.error?.message || 'Login failed')
    }
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      setUser(null)
      queryClient.clear()
      toast.success('Logged out successfully')
    },
    onError: (error) => {
      console.error('Logout error:', error)
      // Still clear local state even if API call fails
      setUser(null)
      queryClient.clear()
    }
  })

  // Update user state when profile data changes
  useEffect(() => {
    if (userData) {
      setUser(userData)
    } else if (profileError) {
      setUser(null)
    }
    setLoading(profileLoading)
  }, [userData, profileError, profileLoading])

  // Initial loading check
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      setLoading(false)
      setUser(null)
    }
  }, [])

  const contextValue: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login: async (credentials: LoginCredentials) => {
      await loginMutation.mutateAsync(credentials)
    },
    logout: async () => {
      await logoutMutation.mutateAsync()
    },
    refetch: refetchProfile
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}